import { createContext, useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { assinarCamadas, haCamadaAberta } from './sobreposicao';

type Tipo = 'sucesso' | 'erro' | 'info';
interface ItemToast {
  id: number;
  tipo: Tipo;
  texto: string;
  /** U15 F8: ação opcional do aviso (o "Ver tarefa" do atalho rápido); o aviso fecha ao acioná-la. */
  acao?: AcaoToast;
}
export interface AcaoToast {
  rotulo: string;
  aoAcionar: () => void;
}

const Ctx = createContext<{ mostrar: (tipo: Tipo, texto: string, acao?: AcaoToast) => void; devolverFoco: () => void } | null>(null);
let seq = 0;
// Review r27 spec (O6): o aviso informativo é texto para ler (visão não aplicada, registros recusados): 7 s, como o erro.
// O tempo pausa com o mouse ou o foco na região, como antes.
const DURACAO: Record<Tipo, number> = { sucesso: 4000, info: 7000, erro: 7000 };

/**
 * Esc com o foco num aviso fecha só o aviso (r6 visual B8). As camadas (Modal, Drawer) escutam o Esc no `window`: sem o
 * `stopPropagation`, o Esc no aviso fechava o modal de baixo. Review r25 visual (M1): o aviso fixo da W39 usa o mesmo.
 */
export function escFechaAviso(fechar: () => void) {
  return (e: { key: string; stopPropagation: () => void }) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    fechar();
  };
}

/**
 * O aviso focado vai sumir: devolve o foco a quem o tinha antes do F8 (remover o elemento focado não dispara focusout, e o
 * foco cairia no body). Só quando o foco está na região dos avisos e o anterior ainda está na página.
 */
export function devolverFocoAntes(regiao: { contains: (n: Node | null) => boolean } | null, ativo: Element | null, antes: { current: { isConnected: boolean; focus: () => void } | null }): boolean {
  if (!regiao?.contains(ativo) || !antes.current?.isConnected) return false;
  antes.current.focus();
  antes.current = null;
  return true;
}

/** Um aviso: o tempo para enquanto a região está pausada (mouse em cima ou foco dentro) e recomeça de onde parou. */
function Aviso({ item, pausado, aoFechar }: { item: ItemToast; pausado: boolean; aoFechar: (id: number) => void }) {
  const { t } = useTranslation();
  const restante = useRef(DURACAO[item.tipo]);
  useEffect(() => {
    if (pausado) return;
    const inicio = Date.now();
    const id = setTimeout(() => aoFechar(item.id), Math.max(0, restante.current));
    return () => {
      clearTimeout(id);
      restante.current -= Date.now() - inicio;
    };
  }, [pausado, item.id, aoFechar]);
  return (
    <div
      data-toast
      className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border border-borda bg-sup-2 p-3 text-sm shadow-lg"
      // Esc com o foco no aviso fecha só o aviso (não o modal de baixo).
      onKeyDown={escFechaAviso(() => aoFechar(item.id))}
    >
      {/* Review r27 spec (O6): o informativo tem o ícone próprio ("i"); antes saía com o check verde do sucesso. */}
      {item.tipo === 'erro' ? (
        <CircleAlert className="mt-0.5 size-4 shrink-0 text-perigo" aria-hidden />
      ) : item.tipo === 'info' ? (
        <Info className="mt-0.5 size-4 shrink-0 text-ciano" aria-hidden data-toast-info />
      ) : (
        <CircleCheck className="mt-0.5 size-4 shrink-0 text-verde" aria-hidden />
      )}
      <p className="min-w-0 flex-1 break-words text-tinta-1">{item.texto}</p>
      {item.acao && (
        <button
          type="button"
          data-acao-aviso
          onClick={() => {
            item.acao?.aoAcionar();
            aoFechar(item.id);
          }}
          className="shrink-0 rounded font-medium text-acento-forte underline hover:text-tinta-1"
        >
          {item.acao.rotulo}
        </button>
      )}
      <button type="button" data-fechar-aviso aria-label={t('avisos.fechar')} onClick={() => aoFechar(item.id)} className="rounded text-tinta-3 hover:text-tinta-1">
        <X className="size-4" />
      </button>
    </div>
  );
}

/**
 * Avisos (r6 visual B8): região `aria-live` fora do trap das camadas (`data-livre-do-trap`), então o fechar fica alcançável com
 * o modal aberto pelo atalho F8 (vai ao aviso mais recente); o tempo pausa com o mouse em cima e com o foco dentro; Esc no aviso
 * fecha só ele; ao fechar, o foco volta para onde estava antes do F8.
 */
export function ProvedorToast({ children, fixos }: { children: ReactNode; /** Fase 2 (W39): avisos que não somem sozinhos (nova versão), antes dos curtos. */ fixos?: ReactNode }) {
  // r9 visual B3: com o hook, o nome da região troca ao vivo com o idioma (antes ficava no idioma do primeiro render).
  const { t } = useTranslation();
  const [itens, setItens] = useState<ItemToast[]>([]);
  const [mouse, setMouse] = useState(false);
  const [foco, setFoco] = useState(false);
  const regiao = useRef<HTMLDivElement>(null);
  const camadaAberta = useSyncExternalStore(assinarCamadas, haCamadaAberta, haCamadaAberta);
  const focoAntes = useRef<HTMLElement | null>(null);

  const devolverFoco = useCallback(() => void devolverFocoAntes(regiao.current, document.activeElement, focoAntes), []);
  const remover = useCallback(
    (id: number) => {
      devolverFoco();
      setItens((l) => l.filter((i) => i.id !== id));
    },
    [devolverFoco],
  );
  const mostrar = useCallback((tipo: Tipo, texto: string, acao?: AcaoToast) => {
    const id = ++seq;
    setItens((l) => [...l.slice(-3), { id, tipo, texto, acao }]);
  }, []);

  useEffect(() => {
    const atalho = (e: KeyboardEvent) => {
      if (e.key !== 'F8') return;
      // Review r28 robustez (B1): o aviso fixo escondido pela camada aberta não recebe o foco.
      const botoes = [...(regiao.current?.querySelectorAll<HTMLElement>('[data-fechar-aviso]') ?? [])].filter((b) => !b.closest('[hidden]'));
      const ultimo = botoes[botoes.length - 1];
      if (!ultimo) return;
      e.preventDefault();
      if (!regiao.current?.contains(document.activeElement)) focoAntes.current = document.activeElement as HTMLElement | null;
      ultimo.focus();
    };
    document.addEventListener('keydown', atalho);
    return () => document.removeEventListener('keydown', atalho);
  }, []);

  // Sem avisos, a pausa por foco não pode ficar presa.
  useEffect(() => {
    if (!itens.length) {
      setFoco(false);
      setMouse(false);
    }
  }, [itens.length]);

  return (
    <Ctx.Provider value={{ mostrar, devolverFoco }}>
      {children}
      <div
        ref={regiao}
        data-livre-do-trap
        role="region"
        aria-label={t('avisos.regiao')}
        onMouseEnter={() => setMouse(true)}
        onMouseLeave={() => setMouse(false)}
        onFocus={() => setFoco(true)}
        onBlur={(e) => !regiao.current?.contains(e.relatedTarget as Node | null) && setFoco(false)}
        className="pointer-events-none fixed top-4 right-4 left-4 z-60 flex flex-col items-end gap-2 sm:top-auto sm:bottom-4 sm:left-auto"
      >
        <div aria-live="polite" className="flex w-full flex-col items-end gap-2">
          {/* Review r25 visual (B1): no celular os avisos curtos ficam no topo, mas o fixo (que só sai no X) ia para cima do botão
              do menu até ser fechado; ele fica embaixo, na posição que todos os avisos já têm no desktop. */}
          {/* Review r28 robustez (B1): com Modal ou Drawer aberto, o aviso fixo cobria o rodapé (o "Salvar") e o campo da camada,
              a 390, a 360 e a 1440 (no Drawer à direita). Ele espera a camada fechar (`hidden`, sem desmontar: a vigia da versão
              segue) e volta no mesmo lugar; o próprio texto pede para salvar antes o que estiver editando. Os avisos curtos (o
              erro do Salvar, por exemplo) seguem por cima da camada. */}
          {fixos && (
            <div data-avisos-fixos hidden={camadaAberta} className="fixed right-4 bottom-4 left-4 flex flex-col items-end gap-2 empty:hidden sm:static sm:w-full">
              {fixos}
            </div>
          )}
          {itens.map((i) => (
            <Aviso key={i.id} item={i} pausado={mouse || foco} aoFechar={remover} />
          ))}
        </div>
      </div>
    </Ctx.Provider>
  );
}

/**
 * Fase 2 (W39) · review r25 visual (M1): fechar de um aviso fixo (nova versão), com a mesma devolução de foco dos avisos curtos
 * (o foco volta para onde estava antes do F8, e não para o body).
 */
export function useFecharAvisoFixo(fechar: () => void) {
  const c = useContext(Ctx);
  const devolver = c?.devolverFoco;
  return useCallback(() => {
    devolver?.();
    fechar();
  }, [devolver, fechar]);
}

export function useToast() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useToast fora do provedor');
  return {
    sucesso: (t: string, acao?: AcaoToast) => c.mostrar('sucesso', t, acao),
    erro: (t: string) => c.mostrar('erro', t),
    info: (t: string) => c.mostrar('info', t),
  };
}
